import { useApolloClient } from '@apollo/client';
import React, { useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import {
  SlaFailureReasonKey,
  SlaFailureReasonMetadata,
  SlaFailureReasons,
} from '@eluve/api-contract';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  Icon,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { formatToLocale } from '@eluve/date-utils';

interface SlaFailureReasonTag {
  __typename: 'AppointmentTags';
  id: string;
  metadata: unknown;
  createdAt: string;
  tag: {
    __typename: 'AppointmentTagOptions';
    id: string;
    tag: string;
  };
}

interface SlaFailureReasonSectionProps {
  tenantId: string;
  appointmentId: string;
  hasFailedSla: boolean | null;
  existingTag: SlaFailureReasonTag | null;
}

export const SlaFailureReasonSection: React.FC<
  SlaFailureReasonSectionProps
> = ({ tenantId, appointmentId, hasFailedSla, existingTag }) => {
  const api = useApiClient();
  const apolloClient = useApolloClient();
  const toast = useToast();

  const [selectedReason, setSelectedReason] =
    useState<SlaFailureReasonKey | null>(null);
  const [customReason, setCustomReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (hasFailedSla !== true) {
    return null;
  }

  const existingMetadata = existingTag?.metadata as
    | SlaFailureReasonMetadata
    | undefined;

  const handleSubmit = async () => {
    if (!selectedReason) {
      toast.error('Please select a reason');
      return;
    }

    if (selectedReason === 'OTHER' && !customReason.trim()) {
      toast.error('Please provide a custom reason');
      return;
    }

    setIsSubmitting(true);

    const metadata: SlaFailureReasonMetadata = {
      reason: selectedReason,
      ...(selectedReason === 'OTHER' && { customReason: customReason.trim() }),
    };

    const result = await api.appointments.addAppointmentTag({
      params: { tenantId, appointmentId },
      body: {
        type: 'HumanApplied',
        key: 'SLA_FAILURE_REASON',
        metadata,
      },
    });

    if (result.status === 200) {
      await apolloClient.refetchQueries({
        include: 'active',
      });
      setSelectedReason(null);
      setCustomReason('');
    } else {
      toast.error('Failed to add SLA failure reason');
    }

    setIsSubmitting(false);
  };

  const getDisplayReason = () => {
    if (!existingMetadata) return null;

    const reasonKey = existingMetadata.reason as SlaFailureReasonKey;
    if (reasonKey === 'OTHER' && existingMetadata.customReason) {
      return `Other: ${existingMetadata.customReason}`;
    }
    return SlaFailureReasons[reasonKey] ?? existingMetadata.reason;
  };

  return (
    <Card className="my-4 w-full rounded-xl border-border-warning-subtle">
      <CardHeader className="flex h-10 flex-row place-items-center justify-between space-y-0 rounded-t-xl border-b bg-bg-warning-subtle p-0 px-5 tracking-normal">
        <Stack align="center" gap={2} direction="horizontal">
          <Icon name="AlertTriangle" className="text-text-warning-subtle" />
          <Text size="md">SLA Failure Reason</Text>
        </Stack>
      </CardHeader>
      <CardContent className="p-5">
        {existingTag ? (
          <Stack gap={2}>
            <Stack gap={2} direction="horizontal">
              <Text size="md" inline weight="semibold">
                Reason:
              </Text>
              <Text size="md" inline>
                {getDisplayReason()}
              </Text>
            </Stack>
            <Stack gap={2} direction="horizontal">
              <Text size="md" inline weight="semibold">
                Added:
              </Text>
              <Text size="md" inline>
                {formatToLocale(existingTag.createdAt)}
              </Text>
            </Stack>
          </Stack>
        ) : (
          <Stack gap={4}>
            <Alert variant="warning">
              <Icon name="AlertTriangle" />
              <AlertTitle>SLA Breach Detected</AlertTitle>
              <AlertDescription>
                This appointment failed to meet the note generation SLA. Please
                document the reason for tracking and analysis purposes.
              </AlertDescription>
            </Alert>

            <Stack gap={2}>
              <Text size="md" weight="semibold">
                Select failure reason:
              </Text>
              <Select
                value={selectedReason ?? undefined}
                onValueChange={(value) =>
                  setSelectedReason(value as SlaFailureReasonKey)
                }
              >
                <SelectTrigger className="w-full">
                  {selectedReason
                    ? SlaFailureReasons[selectedReason]
                    : 'Select a reason...'}
                </SelectTrigger>
                <SelectContent>
                  {(
                    Object.entries(SlaFailureReasons) as [
                      SlaFailureReasonKey,
                      string,
                    ][]
                  ).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Stack>

            {selectedReason === 'OTHER' && (
              <Stack gap={2}>
                <Text size="md" weight="semibold">
                  Custom reason:
                </Text>
                <Input
                  placeholder="Describe the failure reason..."
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                />
                <Text size="xs" color="tertiary">
                  If this reason is used frequently, consider requesting a
                  backfill to add it as a standard option in the codebase.
                </Text>
              </Stack>
            )}

            <Button
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                !selectedReason ||
                (selectedReason === 'OTHER' && !customReason.trim())
              }
            >
              {isSubmitting ? 'Saving...' : 'Save Reason'}
            </Button>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};
